import Joi from 'joi'
import clsx from 'clsx'
import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import Avatar from 'react-avatar'
import { connect } from 'react-redux'
import { toast } from 'react-toastify'
import { useForm } from 'react-hook-form'
import { joiResolver } from '@hookform/resolvers/joi'
import { useTranslation } from 'react-i18next'

import http from '../../../../shared/http'
import { TApi } from '../../../../shared/const'
import { invitationValidity } from '../../../../shared/helpers'
import { schemaOptions } from '../../../../shared/i18n'
import { types } from '../../../../shared/eventEmitter'

import Btn from '../../../../UI/Btn'
import TextField from '../../../../UI/TextField'
import BtnTooltip from '../../../../UI/BtnTooltip'
import StatusIcon from '../../../../UI/StatusIcon'
import BaseModal from '../../../../UI/BaseModal/BaseModal'

import styles from './style.module.scss'
import { updateProfile } from '../../../../redux/ducks/user'
import { useAvatarUrls } from '../../../../shared/useAvatarUrls'

const schema = Joi.object({
	email: Joi.string().email({ tlds: false }).required()
})

function AddEmployeeModal(props) {
	const { t } = useTranslation()
	const {
		handleSubmit,
		register,
		trigger,
		watch,
		reset,
		formState: { errors, dirtyFields }
	} = useForm({
		resolver: joiResolver(schema, {
			errors: {
				labels: false,
				language: localStorage.getItem('i18nextLng')
			},
			...schemaOptions
		})
	})

	const [employees, setEmployees] = useState([])
	const [invitationUser, setInvitationUser] = useState([])

	// Bilder werden angemeldet geholt (siehe shared/useAvatarUrls) — ein <img src>
	// könnte den Anmelde-Kopfeintrag nicht mitschicken.
	const avatarUrls = useAvatarUrls(employees.map(item => item.avatarHashName))
	const [isLoading, setLoading] = useState(false)
	const [error, setError] = useState('')
	const [onRefresh, setOnRefresh] = useState(null)

	useEffect(() => {
		if (Object.keys(errors).length) trigger()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [localStorage.getItem('i18nextLng')])

	useEffect(() => {
		setError('')
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [watch('email')])

	const loadEmployees = () =>
		http.get(TApi.STUDENT_GET_EMPLOYEES).then(({ data }) => {
			setEmployees(data.employees)
			setInvitationUser(data.invitationUser)
		})

	/*
	 * Die Liste dahinter mitziehen. Eine ausgesprochene Einladung erscheint dort
	 * als eigene Zeile — bis 08.08.2026 wurde nur bei einer bereits angemeldeten
	 * Person aufgefrischt, eine frische Einladung tauchte deshalb erst nach dem
	 * Neuladen der Seite auf und wurde ein zweites Mal ausgesprochen.
	 */
	const refreshList = () => {
		if (onRefresh) onRefresh()
	}

	const onSubmit = form => {
		setLoading(true)

		http.post(TApi.STUDENT_POST_INVITE_EMPLOYEE, {
			...form,
			email: form.email.toLowerCase()
		})
			.then(({ data }) => {
				setError('')
				reset()
				refreshList()

				if (data.status === 'REGISTERED') {
					return setEmployees([...employees, data])
				}
				setInvitationUser([...invitationUser, data])

				toast.success(t('Invitation was sent successfully'))
			})
			.finally(() => {
				setLoading(false)
			})
	}

	const handleOpen = onRefresh => {
		setOnRefresh(() => onRefresh)
		loadEmployees()
	}

	const handleDeleteInviteUser = id => {
		http.delete(TApi.STUDENT_DELETE_INVITE_EMPLOYEE(id)).then(() => {
			setError('')
			setInvitationUser(invitationUser.filter(user => user.id !== id))
			refreshList()
		})
	}

	const handleResendInvite = id => {
		http.post(TApi.STUDENT_RESEND_INVITE(id)).then(() => {
			toast.success(t('Invitation resent successfully'))
			refreshList()
			loadEmployees()
		})
	}

	const makeCEO = userId => {
		setLoading(true)
		http.put(TApi.STUDENT_MAKE_CEO(userId))
			.then(() => {
				toast.success(t('CEO Updated'))
				props.updateProfile({ isCEO: false })
			})
			.finally(() => {
				setLoading(false)
				refreshList()
				loadEmployees()
			})
	}

	const deleteEmployee = userId => {
		setLoading(true)
		http.delete(TApi.STUDENT_DELETE_EMPLOYEE(userId))
			.then(() => {
				toast.success(t('Employee successfully deleted'))
			})
			.finally(() => {
				setLoading(false)
				refreshList()
				loadEmployees()
			})
	}

	return (
		<BaseModal
			onOpen={handleOpen}
			nameEvent={types.openAddEmployeeModal}
			isDirty={Boolean(Object.keys(dirtyFields).length)}
			title={t('Add Employee')}
			isLoading={isLoading}
			fitViewport
			maxWidth={720}
		>
			{() => (
				<div className={styles.root}>
					{/*
					 * Zuerst das Einladen: dafür wird das Fenster geöffnet. Die
					 * bestehende Belegschaft steht darunter — sie sagt vor allem,
					 * wen man nicht noch einmal einladen muss.
					 */}
					<div className={styles.inviteBox}>
						<h4 className={styles.inviteTitle}>{t('Invite new employee by Email')}</h4>
						<p className={styles.inviteText}>
							{t(
								'The person receives an email with a registration link. Until they have registered, the invitation is shown in the employee list.'
							)}
						</p>
						<form onSubmit={handleSubmit(onSubmit)} className={styles.inviteForm}>
							<div className={styles.inviteField}>
								<TextField
									disabled={isLoading}
									error={errors.email}
									register={register('email')}
									placeholder={t('Employee Email')}
								/>
							</div>

							<Btn
								loading={isLoading}
								className={styles.inviteBtn}
								type="submit"
								icon="send"
								iconSize={{ w: 12, h: 12 }}
								iconColor="#fff"
							>
								{t('Invite')}
							</Btn>
						</form>
						{!!error && <span className={styles.errorMessage}>{error}</span>}
					</div>

					<div>
						<p className={styles.sectionTitle}>{t('Company Employee')}</p>
						<div className={styles.people}>
							{employees.map(item => {
								return (
									<div className={styles.person} key={item.id}>
										<div className={styles.personMain}>
											<Avatar
												round
												size={32}
												name={
													item.firstName ? `${item.firstName} ${item.lastName}` : item.email
												}
												src={avatarUrls[item.avatarHashName] || ''}
											/>
											<div className={styles.personName}>
												{item.firstName ? `${item.firstName} ${item.lastName}` : item.email}
												{props.userId === item.id && (
													<span className={styles.self}>({t('You')})</span>
												)}
												{item.isCEO && <span className={styles.tag}>{t('CEO')}</span>}
											</div>
										</div>

										<div />

										<BtnTooltip
											items={[
												{
													label: t('Make CEO'),
													onClick: () => makeCEO(item.id),
													disabled: item.isCEO
												},
												{
													label: t('Delete employee'),
													className: styles.btnRemove,
													onClick: () => deleteEmployee(item.id)
												}
											]}
										/>
									</div>
								)
							})}
							{invitationUser.map(item => {
								const validity = invitationValidity(item.invitedAt)

								return (
									<div className={styles.person} key={item.id}>
										<div className={styles.personMain}>
											<Avatar round size={32} name={item.email} />
											<div className={styles.personName}>{item.email}</div>
										</div>

										<div className={clsx(styles.status, validity?.expired && styles.statusExpired)}>
											<StatusIcon name="invited" size={16} />
											<span>
												{t('Invited')}
												{!!validity && (
													<span className={styles.validity}>
														{' · '}
														{validity.expired
															? t('Expired')
															: t('Valid for {{days}} more days', {
																	days: validity.daysLeft
															  })}
													</span>
												)}
											</span>
										</div>

										<BtnTooltip
											items={[
												{
													label: t('Resend invitation'),
													onClick: () => handleResendInvite(item.id)
												},
												{
													label: t('Delete invitation'),
													className: styles.btnRemove,
													onClick: () => handleDeleteInviteUser(item.id)
												}
											]}
										/>
									</div>
								)
							})}
						</div>
					</div>
				</div>
			)}
		</BaseModal>
	)
}

AddEmployeeModal.propTypes = {
	userId: PropTypes.number,
	isCEO: PropTypes.bool
}

const mapStateToProps = state => ({
	userId: state.user.profile.id,
	isCEO: state.user.profile.isCEO
})

const mapDispatchToProps = dispatch => ({
	updateProfile: data => dispatch(updateProfile(data))
})

export default connect(mapStateToProps, mapDispatchToProps)(AddEmployeeModal)
